import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _once from "lodash/once";
var _excluded = ["name", "label", "InputComponent"],
  _excluded2 = ["heading", "subheading"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Checkbox, Input, SocialButton, ContentDivider, DialogContent, FormText } from '@milanote/roma';
import { Eye, EyeOff } from '@untitledui/icons';
import { sendRegisterFormStartEvent, sendRegisterSubmitEvent } from '../../../../../../../client/analytics/dataLayerService';
import FinalField from '../../../../../components/form/components/finalForm/FinalField';
import FinalForm from '../../../../../components/form/components/finalForm/FinalForm';
import { isFormInvalid, isFormSubmitting } from '../../../../../components/form/components/finalForm/finalFormUtils';
import FormError from '../../../../../components/form/components/FormError';
import formSubmissionExceptionHandler from '../../../../../components/form/registration/formSubmissionExceptionHandler';
import useGetSocialOauthError from '../../../../../components/form/registration/hooks/useGetSocialOauthError';
import { fetchInvitationEmail, submitForm } from '../../../../../components/form/registration/registerFormUtils';
import { getInitialValuesFromQueryString, getSocialSignInUrl } from '../../../../../components/form/registration/registrationUtils';
import { REGISTER_FIELDS } from '../../../../../components/form/validation/validationConstants';
import { REGISTRATION_SOURCES } from '../../../../../../../client/analytics/dataLayerConstants';
import { isMobilePlatform } from '../../../../../../util/getPlatform';
import { AppStoreButton } from '../../appStoreButton/AppStoreButton';
import { useTranslator } from '../../../../../../locales/useTranslator';
import '@milanote/roma/styles.css';

// Utitities used by the form
var FORM_FIELDS = Object.values(REGISTER_FIELDS);
var sendRegisterFormStart = _once(sendRegisterFormStartEvent);

// Wrapper component to hook Roma forms up to FinalForm

var LabeledField = function LabeledField(_ref) {
  var name = _ref.name,
    label = _ref.label,
    _ref$InputComponent = _ref.InputComponent,
    InputComponent = _ref$InputComponent === void 0 ? Input : _ref$InputComponent,
    others = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(FinalField, {
    field: REGISTER_FIELDS[name]
  }, function (_ref2) {
    var _display$error;
    var input = _ref2.input,
      display = _ref2.display;
    return /*#__PURE__*/React.createElement(InputComponent, _extends({
      size: "md",
      label: label,
      placeholder: "",
      name: name
    }, input, {
      isInvalid: !!display.error,
      hint: (_display$error = display.error) === null || _display$error === void 0 ? void 0 : _display$error.message
    }, others));
  });
};

// Password field with show/hide toggle
var PasswordField = function PasswordField(props) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showPassword = _useState2[0],
    setShowPassword = _useState2[1];
  var passwordInputRef = React.useRef(null);
  var PasswordToggleButton = useMemo(function () {
    return function (props) {
      return /*#__PURE__*/React.createElement(Button, _extends({}, props, {
        color: "link-gray",
        size: "sm",
        iconLeading: showPassword ? EyeOff : Eye,
        onClick: function onClick() {
          var _passwordInputRef$cur;
          setShowPassword(!showPassword);
          (_passwordInputRef$cur = passwordInputRef.current) === null || _passwordInputRef$cur === void 0 || _passwordInputRef$cur.focus();
        },
        "aria-label": showPassword ? 'Hide password' : 'Show password'
      }));
    };
  }, [showPassword]);
  return /*#__PURE__*/React.createElement(Input, _extends({}, props, {
    key: "password-input",
    type: showPassword ? 'text' : 'password',
    ref: passwordInputRef,
    iconTrailing: PasswordToggleButton
  }));
};

// Links for T&Cs
var FinePrintLink = function FinePrintLink(_ref3) {
  var href = _ref3.href,
    children = _ref3.children;
  return /*#__PURE__*/React.createElement("a", {
    href: href,
    target: "_blank",
    rel: "noopener noreferrer",
    onClick: function onClick(e) {
      e.stopPropagation();
    }
  }, children);
};
var SignupFormHeader = function SignupFormHeader(_ref4) {
  var heading = _ref4.heading,
    subheading = _ref4.subheading,
    localeStrings = _ref4.localeStrings;
  var translate = useTranslator(localeStrings);
  var renderedHeading = heading || translate('register.heading_marketing');
  var renderedSubheading = subheading || translate('register.subheading');
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col items-center gap-3xl"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col items-center gap-md w-full"
  }, /*#__PURE__*/React.createElement("h2", {
    className: "text-center text-display-3xl font-serif font-semibold text-primary"
  }, renderedHeading), /*#__PURE__*/React.createElement("p", {
    className: "text-center text-display-xs text-fg-tertiary font-normal"
  }, renderedSubheading)));
};
var SignupFormContents = function SignupFormContents(_ref5) {
  var buttonText = _ref5.buttonText,
    localeStrings = _ref5.localeStrings;
  var oauthError = useGetSocialOauthError();
  var _useState3 = useState(function () {
      return getInitialValuesFromQueryString();
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    initialValues = _useState4[0],
    setInitialValues = _useState4[1];
  useEffect(function () {
    _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var invitationEmail;
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (initialValues.inviteCode) {
              _context.next = 1;
              break;
            }
            return _context.abrupt("return");
          case 1:
            _context.next = 2;
            return fetchInvitationEmail(initialValues.inviteCode);
          case 2:
            invitationEmail = _context.sent;
            setInitialValues(function (prevInitialValues) {
              return _objectSpread(_objectSpread({}, prevInitialValues), {}, {
                email: invitationEmail
              });
            });
          case 3:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }))();
  }, [initialValues.inviteCode]);
  var onSubmit = useCallback(/*#__PURE__*/function () {
    var _ref7 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(formValues) {
      var _t;
      return _regeneratorRuntime.wrap(function (_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            _context2.next = 1;
            return submitForm(formValues);
          case 1:
            _context2.next = 3;
            break;
          case 2:
            _context2.prev = 2;
            _t = _context2["catch"](0);
            return _context2.abrupt("return", formSubmissionExceptionHandler(_t));
          case 3:
          case "end":
            return _context2.stop();
        }
      }, _callee2, null, [[0, 2]]);
    }));
    return function (_x) {
      return _ref7.apply(this, arguments);
    };
  }(), []);
  var translate = useTranslator(localeStrings);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-lg"
  }, /*#__PURE__*/React.createElement(SocialButton, {
    size: "md",
    theme: "color",
    social: "google",
    href: getSocialSignInUrl('google'),
    onClick: function onClick() {
      return sendRegisterSubmitEvent(REGISTRATION_SOURCES.OAUTH_GOOGLE);
    }
  }, translate('register.social.googleButton')), /*#__PURE__*/React.createElement(SocialButton, {
    size: "md",
    theme: "color",
    social: "apple",
    href: getSocialSignInUrl('apple'),
    onClick: function onClick() {
      return sendRegisterSubmitEvent(REGISTRATION_SOURCES.OAUTH_APPLE);
    }
  }, translate('register.social.appleButton'))), /*#__PURE__*/React.createElement(ContentDivider, {
    type: "single-line",
    className: "-mb-md uppercase"
  }, translate('register.dividerLabel')), /*#__PURE__*/React.createElement(FinalForm, {
    className: "contents",
    onSubmit: onSubmit,
    initialValues: initialValues,
    fields: FORM_FIELDS
  }, function (formState) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col gap-2xl"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex flex-row gap-lg"
    }, /*#__PURE__*/React.createElement(LabeledField, {
      label: translate('register.form.firstNamePlaceholder'),
      name: "givenName",
      onInput: sendRegisterFormStart
    }), /*#__PURE__*/React.createElement(LabeledField, {
      label: translate('register.form.lastNamePlaceholder'),
      name: "familyName"
    })), /*#__PURE__*/React.createElement(LabeledField, {
      label: translate('register.form.emailLabel'),
      name: "email"
    }), /*#__PURE__*/React.createElement(LabeledField, {
      InputComponent: PasswordField,
      label: translate('register.form.passwordLabel'),
      name: "password"
    })), /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col gap-md"
    }, /*#__PURE__*/React.createElement(LabeledField, {
      InputComponent: Checkbox,
      size: "sm",
      name: "agreeToTerms",
      label: /*#__PURE__*/React.createElement("span", {
        className: "text-md"
      }, translate('register.form.agreeToTerms', undefined, {
        components: {
          termsLink: /*#__PURE__*/React.createElement(FinePrintLink, {
            href: "https://milanote.com/legal/terms-of-service"
          }),
          and: /*#__PURE__*/React.createElement("span", {
            className: "and"
          }),
          andText: /*#__PURE__*/React.createElement("span", null),
          privacyLink: /*#__PURE__*/React.createElement(FinePrintLink, {
            href: "https://milanote.com/legal/privacy-policy"
          })
        }
      }))
    }), /*#__PURE__*/React.createElement(LabeledField, {
      InputComponent: Checkbox,
      size: "sm",
      name: "subscribeToProductUpdates",
      label: /*#__PURE__*/React.createElement("span", {
        className: "text-md"
      }, translate('register.form.subscribeToUpdates'), ' ', /*#__PURE__*/React.createElement("span", {
        className: "text-fg-quaternary font-normal"
      }, translate('register.form.subscribeOptional')))
    })), /*#__PURE__*/React.createElement(Button, {
      size: "md",
      color: "primary",
      className: "w-full",
      type: "submit",
      isLoading: isFormSubmitting(formState),
      isDisabled: isFormInvalid(formState)
    }, buttonText || translate('register.form.submitButton_marketing')), /*#__PURE__*/React.createElement(FormError, {
      className: "primary-error",
      error: formState.submitError || oauthError,
      hide: formState.modifiedSinceLastSubmit || formState.submitting
    }), /*#__PURE__*/React.createElement("div", {
      className: "text-center text-md text-fg-secondary"
    }, translate('register.login.text'), ' ', /*#__PURE__*/React.createElement(Button, {
      color: "link-color",
      size: "md",
      href: "https://app.milanote.com/"
    }, translate('register.login.link'))), /*#__PURE__*/React.createElement(FormText, {
      size: "sm",
      color: "quaternary",
      align: "center"
    }, translate('disclaimer.text', undefined, {
      components: {
        termsLink: /*#__PURE__*/React.createElement(FinePrintLink, {
          href: "https://policies.google.com/terms"
        }),
        privacyLink: /*#__PURE__*/React.createElement(FinePrintLink, {
          href: "https://policies.google.com/privacy"
        })
      }
    })));
  }));
};
export var SignupForm = function SignupForm(_ref8) {
  var heading = _ref8.heading,
    subheading = _ref8.subheading,
    props = _objectWithoutProperties(_ref8, _excluded2);
  var isMobile = isMobilePlatform();
  return /*#__PURE__*/React.createElement("div", {
    className: "RomaSignup flex justify-center font-sans"
  }, /*#__PURE__*/React.createElement(DialogContent, {
    className: classNames('SignupFormFieldsModal',
    // semantic classname used for mobile styling
    'self-start flex-col gap-3xl max-w-110', {
      'variant-appstore': isMobile
    })
  }, /*#__PURE__*/React.createElement(SignupFormHeader, {
    heading: heading,
    subheading: subheading,
    localeStrings: props.localeStrings
  }), isMobile ? /*#__PURE__*/React.createElement(AppStoreButton, null) : /*#__PURE__*/React.createElement(SignupFormContents, props)));
};